import { ArrowGo, EmoCart, EmoNeutral, Zoom } from "@assets"
import { Head, router } from "@inertiajs/react"
import { Plus, ShoppingBag, Store } from "lucide-react"
import { useEffect, useState } from "react"

import FeedItem, {
  type FeedItem as FeedItemData,
} from "@/components/feed/feed-item"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/app-layout"
import {
  businessPath,
  marketplacePath,
  marketplaceProductsPath,
  productsNewPath,
} from "@/routes"

interface Business {
  id: number
  business_name: string
  description: string
  business_type: string
  address: string
  phone: string
  website?: string
  is_verified: boolean
  created_at: string
  rating?: number
  total_reviews?: number
  neighborhood: {
    id: number
    name: string
  }
  owner?: {
    id: number
    name: string
    avatar_url?: string
    verified?: boolean
  } | null
  reviews: {
    id: number
    rating: number
  }[]
  images?: {
    url: string
  }[]
  is_favorited?: boolean
  favorites_count?: number
}

type BusinessSort = "created_at" | "rating" | "reviews" | "name"

interface Filters {
  business_type?: string
  search?: string
  sort?: BusinessSort
}

interface Props {
  businesses: Business[]
  business_types: string[]
  filters: Filters
  can_create_business?: boolean
  has_products: boolean
  pagination: {
    current_offset: number
    limit: number
    total: number
    has_more: boolean
  }
}

export default function MarketplaceIndex({
  businesses,
  filters,
  can_create_business,
  has_products,
  pagination,
}: Props) {
  const [searchTerm, setSearchTerm] = useState<string>(filters.search ?? "")
  const [allBusinesses, setAllBusinesses] = useState(businesses)
  const [isLoading, setIsLoading] = useState(false)

  const [previousSearchTerm, setPreviousSearchTerm] = useState<string>("")

  useEffect(() => {
    if (businesses.length > 0) {
      setAllBusinesses((prevBusinesses) => {
        const urlParams = new URLSearchParams(window.location.search)
        const currentOffset = parseInt(urlParams.get("offset") ?? "0")
        const isLoadMore = currentOffset > 0

        if (!isLoadMore) {
          return businesses
        } else {
          const newBusinesses = businesses.filter(
            (newBusiness) =>
              !prevBusinesses.some(
                (existingBusiness) => existingBusiness.id === newBusiness.id,
              ),
          )
          return [...prevBusinesses, ...newBusinesses]
        }
      })
      setIsLoading(false)
    } else if (businesses.length === 0 && !isLoading) {
      setAllBusinesses([])
    }
  }, [businesses, isLoading])

  useEffect(() => {
    if (
      previousSearchTerm.trim() &&
      !searchTerm.trim() &&
      previousSearchTerm !== searchTerm
    ) {
      handleSearch()
    }
    setPreviousSearchTerm(searchTerm)
  }, [searchTerm, previousSearchTerm])

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    setIsLoading(false)
    router.get(
      marketplacePath(),
      {
        ...filters,
        search: searchTerm || undefined,
        offset: 0,
      },
      { preserveState: true },
    )
  }

  const handleLoadMore = () => {
    if (isLoading || !pagination.has_more) return

    setIsLoading(true)
    const nextOffset = pagination.current_offset + pagination.limit

    router.get(
      marketplacePath(),
      {
        ...filters,
        search: searchTerm || undefined,
        offset: nextOffset,
      },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["businesses", "pagination"],
        onFinish: () => setIsLoading(false),
      },
    )
  }

  return (
    <AppLayout>
      <Head title="Negocios" />
      <div className="mx-auto max-w-6xl space-y-[15px] md:p-6">
        <div className="flex flex-col">
          <div className="flex items-center justify-center gap-4">
            <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px] md:text-3xl">
              <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
                <img
                  src={EmoCart}
                  alt="Cart"
                  className="h-full w-full object-contain"
                />
              </span>
              Market
            </h1>

            {can_create_business && (
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  onClick={() => router.visit(businessPath("new"))}
                  className="flex flex-shrink-0 items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  <Store className="h-4 w-4 sm:hidden" />
                  <span className="hidden sm:inline">Crear negocio</span>
                </Button>

                {!has_products && (
                  <Button
                    variant="default"
                    onClick={() => router.visit(productsNewPath())}
                    className="flex flex-shrink-0 items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    <ShoppingBag className="h-4 w-4 sm:hidden" />
                    <span className="hidden sm:inline">Vender producto</span>
                  </Button>
                )}
              </div>
            )}
          </div>

          <p className="mx-10 mt-[8px] text-center text-[13px] font-normal tracking-[0.2px] text-balance">
            Compra y vende productos en la comunidad argentina
          </p>
        </div>

        {/* Tab Navigation */}
        {has_products && (
          <Tabs value="businesses" className="space-y-6">
            <div className="flex justify-center">
              <TabsList className="bg-foreground inline-flex h-auto rounded-[15px] px-[10px] py-[10px] tracking-[0.26px]">
                <TabsTrigger
                  value="businesses"
                  className="data-[state=active]:bg-card flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:font-bold"
                >
                  Negocios
                </TabsTrigger>
                <TabsTrigger
                  value="products"
                  className="data-[state=active]:bg-card flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:font-bold"
                  onClick={() => router.visit(marketplaceProductsPath())}
                >
                  Productos
                </TabsTrigger>
              </TabsList>
            </div>
          </Tabs>
        )}

        {/* Search */}
        <div className="mx-[12px] flex">
          <form
            onSubmit={handleSearch}
            className="bg-card w-full rounded-[12px]"
          >
            <div className="relative flex items-end">
              <Zoom className="text-primary bg-card mx-[10px] my-[6px] h-[34px] fill-current" />
              <Input
                placeholder="Buscar negocios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="placeholder:text-muted-foreground w-full border-0 px-0 pt-0 pb-[6px] text-[15px] font-light tracking-[0.07px] opacity-[0.75] shadow-[0_0_0_0px_rgba(0,0,0,0)] focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <Button
                type="submit"
                size="icon"
                className="text-primary bg-card m-[6px] h-[34px] w-[34px] rounded-[8px] shadow-[0px_0px_4px_#00000029]"
              >
                <ArrowGo className="text-primary size-6 fill-current" />
              </Button>
            </div>
          </form>
        </div>

        {allBusinesses.length > 0 ? (
          <div className="">
            {allBusinesses.map((b) => (
              <FeedItem
                key={b.id}
                variant="card"
                item={
                  {
                    type: "business",
                    id: b.id,
                    source_id: b.id,
                    created_at: b.created_at,
                    name: b.business_name,
                    description: b.description,
                    category: b.business_type,
                    verified: b.is_verified,
                    rating: b.rating,
                    total_reviews: b.total_reviews,
                    neighborhood: b.neighborhood
                      ? { id: b.neighborhood.id, name: b.neighborhood.name }
                      : undefined,
                    owner: b.owner
                      ? {
                          id: b.owner.id,
                          name: b.owner.name,
                          avatar_url: b.owner.avatar_url,
                          verified: b.owner.verified,
                        }
                      : null,
                    is_favorited: b.is_favorited,
                    favorites_count: b.favorites_count,
                  } as FeedItemData
                }
              />
            ))}
          </div>
        ) : (
          <div
            className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
            style={{ background: "var(--gradient-highlight)" }}
          >
            <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
              <img src={EmoNeutral} alt="Sin contenido" />
            </div>

            <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
              No se encontraron negocios
            </h3>
          </div>
        )}
        {/* </Tabs>
        </Tabs> */}
        {/* Load More Button */}
        {allBusinesses.length > 0 && pagination.has_more && (
          <div className="text-center">
            <Button
              variant="ghost"
              onClick={handleLoadMore}
              className="text-primary hover:bg-muted"
            >
              Ver más negocios
            </Button>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
